import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Spinner from "../../../components/Spinner";
import { formatNumber } from "../../../utils/format";
import Modal from "../../../components/Modal";
import ModalContainer from "../components/Table.js/ModalContainer.js";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere";
import { db } from "../../../FirebaseConfig.ts";
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import useInsertDoc from "../../../hooks/useInsertDoc.js";
import useUpdateDoc from "../../../hooks/useUpdateDoc.js";
import Trash from "../../../components/SVG/trash.js";
import EditEmployee from "../components/Table.js/editEmployee.js";

// Deriva la fecha del crédito. El campo `numberCredit` se guarda con
// formato "YYYYMMDDmms", por lo que los primeros 8 dígitos son la fecha.
const formatCreditDate = (el) => {
  const raw = el?.numberCredit;
  if (typeof raw === "string" && raw.length >= 8) {
    const y = raw.slice(0, 4);
    const m = raw.slice(4, 6);
    const d = raw.slice(6, 8);
    return `${d}/${m}/${y}`;
  }
  if (el?.createdAt?.toDate) {
    return el.createdAt.toDate().toLocaleDateString("es-CO");
  }
  return "—";
};

function CreditosTable({ user }) {
  const [data, setData] = useState([]);
  const [items, setItems] = useState([]);
  const [showExcel, setShowExcel] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);
  const { handleInsert } = useInsertDoc();
  const { handleUpdate } = useUpdateDoc();
  const navigate = useNavigate();

  const { data: dataUser, isFetching: fetchingUser } = useSnapshotWhere(
    "users",
    {
      filter: {
        firstCondition: "id",
        secondCondition: "==",
        thirdCondition: user.uid,
      },
    }
  );

  const [isFetching, setIsFetching] = useState(true);
  const [activeSearch, setActiveSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("identity"); // identity, name, lastName, uuid
  const [showModal, setShowModal] = useState(false);
  const [showModalEmployee, setShowModalEmployee] = useState(false);
  const [currentRequest, setCurrentRequest] = useState({});
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  useEffect(() => {
    setIsLoaded(true);
    if (!fetchingUser) {
      getFirstData();
    }
  }, [fetchingUser]);

  const getThemeColors = () => {
    if (hostname === "creditos.sofia.com.co") {
      return {
        primary: "bg-gray-900 hover:bg-gray-800",
        text: "text-gray-900",
        accent: "bg-gray-100",
        border: "border-gray-300",
      };
    }
    return {
      primary: "bg-[#49b0a8] hover:bg-[#3a8a84]",
      text: "text-[#49b0a8]",
      accent: "bg-[#49b0a8]/10",
      border: "border-[#49b0a8]/20",
    };
  };

  const theme = getThemeColors();

  const searchOptions = [
    {
      value: "identity",
      label: "Cédula",
      placeholder: "Buscar por número de cédula...",
    },
    { value: "name", label: "Nombre", placeholder: "Buscar por nombre..." },
    {
      value: "lastName",
      label: "Apellido",
      placeholder: "Buscar por apellido...",
    },
    {
      value: "uuid",
      label: "Número de Crédito",
      placeholder: "Buscar por número de crédito...",
    },
    {
      value: "status",
      label: "Estado",
      placeholder: "Buscar por número de crédito...",
    },
  ];

  const getCurrentSearchOption = () => {
    return (
      searchOptions.find((option) => option.value === searchType) ||
      searchOptions[0]
    );
  };

  const readExcel = (file) => {
    const promise = new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);
      fileReader.onload = (e) => {
        const bufferArray = e.target.result;
        const wb = XLSX.read(bufferArray, { type: "buffer" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        resolve(data);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
    promise.then((d) => {
      setItems(d);
    });
  };

  const fetchCategories = async (status) => {
    // if (!searchValue.trim()) {
    //   setError("Por favor ingresa un valor para buscar");
    //   return;
    // }

    try {
      setIsFetching(true);
      setActiveSearch(true);

      const searchField = searchType;
      let searchValueFormatted = searchValue.trim();

      // Para búsquedas por nombre o apellido, convertir a lowercase para búsqueda case-insensitive
      if (searchType === "name" || searchType === "lastName") {
        searchValueFormatted = searchValueFormatted.toLowerCase();
      } else if (status) {
        searchValueFormatted = status;
      }

      const q = query(
        collection(db, "requests"),
        where(searchField, "==", searchValueFormatted),
        where("majorCompanyId", "==", dataUser[0]?.majorCompanyId)
      );
      const data = await getDocs(q);
      const results = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

      // Si no hay resultados y estamos buscando por nombre/apellido, intentar búsqueda parcial
      if (
        results.length === 0 &&
        (searchType === "name" || searchType === "lastName")
      ) {
        // Obtener todos los registros y filtrar localmente para búsqueda parcial
        const allQuery = query(
          collection(db, "requests"),
          where("status", "==", "aprobado"),
          where("majorCompanyId", "==", dataUser[0]?.majorCompanyId)
        );
        const allData = await getDocs(allQuery);
        const allResults = allData.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        const filteredResults = allResults.filter((item) => {
          const fieldValue = item[searchType]?.toLowerCase() || "";
          return fieldValue.includes(searchValueFormatted.toLowerCase());
        });

        setData(filteredResults);
      } else {
        setData(results);
      }
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    } finally {
      setIsFetching(false);
    }
  };

  const getFirstData = async () => {
    try {
      setIsFetching(true);
      const q = query(
        collection(db, "requests"),
        where("status", "==", "aprobado"),
        where("majorCompanyId", "==", dataUser[0]?.majorCompanyId)
      );
      const subscriber = onSnapshot(q, {
        next: (snapshot) => {
          const data = [];
          snapshot.docs.forEach((doc) => {
            data.push({
              id: doc.id,
              ...doc.data(),
            });
          });
          setData(data);
          setIsFetching(false);
        },
      });
      return () => subscriber();
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    } finally {
      setIsFetching(false);
    }
  };

  const handleInsertExcel = async () => {
    setUploading(true);
    for (const item of items) {
      const res = await handleInsert("requests", {
        ...item,
        ammount: `${item.ammount}`,
        knowMoney: true,
        majorCompanyId: dataUser[0]?.majorCompanyId,
        payments: [],
        cert_laboral: "",
        codeudor: "",
        comp_nomina: "",
        company: "",
        companyId: "",
        dec_renta: "",
        fot_cedula: "",
        status: "aprobado",
      });
      await handleUpdate("requests", res.id, { uuid: res.id });
    }
    setUploading(false);
    setFinished(true);
  };

  const handleDeleteCredit = async (creditId) => {
    if (window.confirm("¿Está seguro de que desea eliminar este crédito?")) {
      try {
        await handleUpdate("requests", creditId, { status: "eliminado" });
        getFirstData();
      } catch (error) {
        console.error("Error deleting credit:", error);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        dot: "bg-yellow-400",
        label: "Pendiente",
      },
      aprobado: {
        bg: "bg-green-100",
        text: "text-green-800",
        dot: "bg-green-400",
        label: "Aprobado",
      },
      rechazado: {
        bg: "bg-red-100",
        text: "text-red-800",
        dot: "bg-red-400",
        label: "Rechazado",
      },
    };

    const config = statusConfig[status] || statusConfig.aprobado;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></div>
        {config.label}
      </span>
    );
  };

  const clearSearch = () => {
    getFirstData();
    setActiveSearch(false);
    setSearchValue("");
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div
        className={`transition-all duration-700 ease-out ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="space-y-1 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            Créditos Aprobados
          </h1>
          <p className="text-gray-600">
            Gestiona y consulta todos los créditos aprobados en el sistema
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className={theme.text}
              >
                <path
                  d="M11 6C13.7614 6 16 8.23858 16 11M16.6588 16.6549L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Búsqueda de Créditos
            </h2>

            {/* Search Type Selector */}
            <div className="flex flex-wrap gap-2">
              {searchOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSearchType(option.value);
                    setSearchValue("");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    searchType === option.value
                      ? `${theme.primary} text-white shadow-md`
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {getCurrentSearchOption().value === "status" ? (
                <div className="flex gap-3 ml-14">
                  <button
                    onClick={() => {
                      fetchCategories("aprobado");
                      setSearchValue("aprobado");
                    }}
                    className={`px-6 rounded-lg font-medium transition-all duration-200 hover:shadow-md ${
                      isFetching
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : `${
                            searchValue === "aprobado"
                              ? `${theme.primary}`
                              : "border border-1 !text-black"
                          } text-white`
                    }`}
                  >
                    {isFetching ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Buscando...</span>
                      </div>
                    ) : (
                      "Aprobado"
                    )}
                  </button>
                  {/* <button
                    onClick={() => {
                      fetchCategories("eliminado");
                      setSearchValue("eliminado");
                    }}
                    className={`px-6 rounded-lg font-medium transition-all duration-200 hover:shadow-md ${
                      isFetching
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : `${
                            searchValue === "eliminado"
                              ? `${theme.primary}`
                              : "border border-1 !text-black"
                          } text-white`
                    }`}
                  >
                    {isFetching ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Buscando...</span>
                      </div>
                    ) : (
                      "Eliminado"
                    )}
                  </button> */}
                </div>
              ) : (
                <>
                  <input
                    type="search"
                    className="block w-full pl-12 pr-32 py-4 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                    placeholder={getCurrentSearchOption().placeholder}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && fetchCategories()}
                  />
                  <button
                    onClick={fetchCategories}
                    disabled={!searchValue.trim() || isFetching}
                    className={`absolute right-2 top-2 bottom-2 px-6 rounded-lg font-medium transition-all duration-200 hover:shadow-md ${
                      !searchValue.trim() || isFetching
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : `${theme.primary} text-white`
                    }`}
                  >
                    {isFetching ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Buscando...</span>
                      </div>
                    ) : (
                      "Buscar"
                    )}
                  </button>
                </>
              )}
            </div>

            {activeSearch && (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-blue-600"
                  >
                    <path
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-sm text-blue-800">
                    Búsqueda activa por{" "}
                    <strong>
                      {getCurrentSearchOption().label.toLowerCase()}
                    </strong>
                    : "{searchValue}"
                  </span>
                </div>
                <button
                  onClick={clearSearch}
                  className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Limpiar filtros
                </button>
              </div>
            )}
          </div>

          {/* Excel Upload Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-green-600"
              >
                <path
                  d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points="14,2 14,8 20,8"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="16"
                  y1="13"
                  x2="8"
                  y2="13"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="16"
                  y1="17"
                  x2="8"
                  y2="17"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <polyline
                  points="10,9 9,9 8,9"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              Importar desde Excel
            </h2>

            {!showExcel ? (
              <button
                onClick={() => setShowExcel(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-md transform hover:scale-105"
              >
                Insertar a través de Excel
              </button>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Insertar a través de Excel</span>
                  <a
                    className="text-blue-600 hover:text-blue-800 font-medium"
                    target="_blank"
                    rel="noreferrer"
                    href="https://klazia.nyc3.digitaloceanspaces.com/plataforma/ejemplolendy_62d678ccea.xlsx"
                  >
                    Descargar Ejemplo
                  </a>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Seleccionar archivo Excel
                    </label>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) readExcel(file);
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#49b0a8] file:text-white hover:file:bg-[#3a8a84] file:cursor-pointer"
                    />
                  </div>

                  {items.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>{items.length}</strong> registros cargados y
                        listos para importar
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleInsertExcel}
                      disabled={uploading || items.length === 0}
                      className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                        uploading || items.length === 0
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white hover:shadow-md transform hover:scale-105"
                      }`}
                    >
                      {uploading ? "Subiendo..." : "Subir Excel"}
                    </button>

                    <button
                      onClick={() => {
                        setShowExcel(false);
                        setItems([]);
                        setFinished(false);
                      }}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200"
                    >
                      Cancelar
                    </button>
                  </div>

                  {uploading && (
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span>Procesando archivo...</span>
                    </div>
                  )}

                  {finished && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        ✅ Se han subido los créditos satisfactoriamente
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal showModal={showModal} setShowModal={setShowModal}>
        <ModalContainer
          currentRequest={currentRequest}
          majorCompanyId={dataUser[0]?.majorCompanyId}
          setCurrentRequest={setCurrentRequest}
        />
      </Modal>

      <Modal showModal={showModalEmployee} setShowModal={setShowModalEmployee}>
        <EditEmployee
          currentRequest={currentRequest}
          majorCompanyId={dataUser[0]?.majorCompanyId}
          setCurrentRequest={setCurrentRequest}
        />
      </Modal>

      {/* Table Section */}
      <div
        className={`transition-all duration-700 ease-out delay-300 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        {isFetching ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <Spinner />
                <p className="text-gray-600">Cargando créditos...</p>
              </div>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="text-center py-12 space-y-4">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-gray-400"
                >
                  <path
                    d="M2 14C2 10.2288 2 8.34315 3.17157 7.17157C4.34315 6 6.22876 6 10 6H14C17.7712 6 19.6569 6 20.8284 7.17157C22 8.34315 22 10.2288 22 14C22 17.7712 22 19.6569 20.8284 20.8284C19.6569 22 17.7712 22 14 22H10C6.22876 22 4.34315 22 3.17157 20.8284C2 19.6569 2 17.7712 2 14Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M12 17.3333C13.1046 17.3333 14 16.5871 14 15.6667C14 14.7462 13.1046 14 12 14C10.8954 14 10 13.2538 10 12.3333C10 11.4129 10.8954 10.6667 12 10.6667M12 17.3333C10.8954 17.3333 10 16.5871 10 15.6667M12 17.3333V18M12 10V10.6667M12 10.6667C13.1046 10.6667 14 11.4129 14 12.3333"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  No hay créditos
                </h3>
                <p className="text-gray-600">
                  {activeSearch
                    ? `No se encontraron créditos con ese ${getCurrentSearchOption().label.toLowerCase()}`
                    : "Aún no hay créditos aprobados"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Results Summary */}
            {activeSearch && (
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
                <p className="text-sm text-gray-700">
                  Se encontraron <strong>{data.length}</strong> resultado
                  {data.length !== 1 ? "s" : ""} para "{searchValue}"
                </p>
              </div>
            )}

            {/* Desktop Table */}
            <div className="hidden lg:block">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Contacto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cédula
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.map((el, index) => (
                    <tr
                      onClick={() =>
                        navigate(
                          `/plataforma/simulador/${el.identity}/${el.uuid}`
                        )
                      }
                      key={el.id}
                      className={`
                        transition-all duration-200 hover:bg-gray-50 group cursor-pointer
                        ${
                          isLoaded
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-4"
                        }
                      `}
                      style={{
                        animationDelay: `${index * 100}ms`,
                        animation: isLoaded
                          ? "fadeInUp 0.6s ease-out forwards"
                          : "none",
                      }}
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600 group-hover:bg-[#49b0a8] group-hover:text-white transition-colors duration-200">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            setShowModalEmployee(true);
                            setCurrentRequest(el);
                            e.stopPropagation();
                          }}
                          className="flex flex-col text-left space-y-1 hover:bg-blue-50 p-2 rounded-lg transition-colors duration-200"
                        >
                          <div className="text-sm font-medium text-blue-600 hover:text-blue-800">{`${el.name} ${el.lastName}`}</div>
                          <div className="text-xs text-gray-500">
                            Click para editar
                          </div>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCreditDate(el)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {el.cellphone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {el.identity}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatNumber(el.ammount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(el.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => {
                              handleDeleteCredit(el.uuid);
                              e.stopPropagation();
                            }}
                            className="text-red-600 hover:text-red-800 p-1 rounded transition-colors duration-200"
                            title="Eliminar crédito"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-4 p-4">
              {data?.map((el, index) => (
                <div
                  key={el.id}
                  className={`
                    bg-white border border-gray-200 rounded-xl p-4 space-y-4 hover:shadow-md transition-all duration-300
                    ${
                      isLoaded
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-4"
                    }
                  `}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isLoaded
                      ? "fadeInUp 0.6s ease-out forwards"
                      : "none",
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <button
                        onClick={() => {
                          setShowModalEmployee(true);
                          setCurrentRequest(el);
                        }}
                        className="text-left"
                      >
                        <h3 className="font-medium text-blue-600 hover:text-blue-800">{`${el.name} ${el.lastName}`}</h3>
                      </button>
                      <p className="text-sm text-gray-500">
                        Cédula: {el.identity}
                      </p>
                      <p className="text-sm text-gray-500">
                        Tel: {el.cellphone}
                      </p>
                      <p className="text-sm text-gray-500">
                        N° Crédito: {el.uuid?.substring(0, 8)}...
                      </p>
                    </div>
                    <div className="text-right space-y-2">
                      <div className="text-lg font-semibold text-gray-900">
                        ${formatNumber(el.ammount)}
                      </div>
                      {getStatusBadge(el.status)}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setShowModal(true);
                        setCurrentRequest(el);
                      }}
                      className="flex-1 bg-[#49b0a8] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3a8a84] transition-colors duration-200"
                    >
                      Ver Solicitud
                    </button>
                    <Link
                      to={`/plataforma/simulador/${el.identity}/${el.uuid}`}
                    >
                      <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors duration-200">
                        Ver Crédito
                      </button>
                    </Link>
                    <button
                      onClick={() => handleDeleteCredit(el.uuid)}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors duration-200"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default CreditosTable;
